import { useState } from "react";

import type { SandboxEntityType } from "../types/entities";
import type { Interaction } from "../types/interactions";

export interface ScoredEntity {
  id: string;
  name: string;
  url: string;
  similarity: number;
  subtitle?: string;
  photo?: string;
  evidence?: string;
  // enriched details for AI prompt
  genres?: string[];
  lineup?: string[];
  type?: string;
  venueName?: string;
  address?: string;
  parents?: string[];
}

export interface EntityRecommendations {
  artists: ScoredEntity[];
  genres: ScoredEntity[];
  venues: ScoredEntity[];
  events: ScoredEntity[];
}

export interface SandboxEvent {
  id: string;
  name: string;
  date: number;
  venue: { id: string; name: string; embedding: number[] };
  genres: Array<{ id: string; name: string; embedding: number[] }>;
  artists: Array<{ id: string; name: string; embedding: number[] }>;
  embedding: number[];
}

export interface ScoredEvent extends SandboxEvent {
  score: number;
  contributors: Array<{
    name: string;
    entityType: SandboxEntityType;
    interactionWeight?: number;
  }>;
}

export interface Persona {
  id: string;
  name: string;
  interactions: Interaction[];
}

export function useSandboxPersonas() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [currentPersona, setCurrentPersona] = useState<Persona | null>(null);

  function newPersona(): Persona {
    const persona: Persona = {
      id: crypto.randomUUID(),
      name: "New persona",
      interactions: [],
    };
    setCurrentPersona(persona);
    return persona;
  }

  function loadPersona(id: string): Persona | null {
    const found = personas.find((p) => p.id === id) ?? null;
    setCurrentPersona(found);
    return found;
  }

  function savePersona(name: string, interactions: Interaction[]): Persona {
    const existing = currentPersona;
    const persona: Persona = existing
      ? { ...existing, name, interactions }
      : { id: crypto.randomUUID(), name, interactions };

    setPersonas((prev) =>
      prev.some((p) => p.id === persona.id)
        ? prev.map((p) => (p.id === persona.id ? persona : p))
        : [...prev, persona],
    );
    setCurrentPersona(persona);
    return persona;
  }

  function deletePersona(id: string): void {
    setPersonas((prev) => prev.filter((p) => p.id !== id));
    if (currentPersona?.id === id) {
      setCurrentPersona(null);
    }
  }

  function exportPersona(persona: Persona): void {
    const stripped: Persona = {
      ...persona,
      interactions: persona.interactions.map(({ entity, ...rest }) => ({
        ...rest,
        entity: {
          ...entity,
          embedding: undefined,
          isLoadingEmbedding: undefined,
          embeddingError: undefined,
        },
      })),
    };
    const json = JSON.stringify(stripped, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `persona-${persona.name.replace(/\s+/g, "-").toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function loadPersonaFromImport(persona: Persona): void {
    const withLoading: Persona = {
      ...persona,
      interactions: persona.interactions.map((i) => ({
        ...i,
        entity: { ...i.entity, embedding: undefined, isLoadingEmbedding: true },
      })),
    };
    setPersonas((prev) => [...prev, withLoading]);
    setCurrentPersona(withLoading);
  }

  return {
    personas,
    currentPersona,
    newPersona,
    loadPersona,
    savePersona,
    deletePersona,
    exportPersona,
    loadPersonaFromImport,
  };
}
